/**
 * ⌘K, on shadcn's `command` (cmdk). Every screen and mutation registers into
 * the registry so the palette always reflects what is on screen; `records`
 * adds universal search — anything in the workspace, found by the server.
 */
import type { LucideIcon } from 'lucide-react'
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Command as CommandRoot,
} from '@/components/ui/command'

export interface Command {
  id: string
  label: string
  group: 'Go to' | 'Create' | 'Actions' | 'Records' | 'Settings'
  icon?: LucideIcon
  keywords?: string
  run: () => void
}

interface CommandContextValue {
  commands: Command[]
  register: (commands: Command[]) => () => void
  open: boolean
  setOpen: (open: boolean) => void
}

const CommandContext = createContext<CommandContextValue | null>(null)

export function useCommandRegistry(): CommandContextValue {
  const ctx = useContext(CommandContext)
  if (!ctx) throw new Error('useCommandRegistry must be used inside <CommandProvider>')
  return ctx
}

/** Pass a MEMOISED array — a fresh array each render re-registers every render. */
export function useRegisterCommands(commands: Command[]): void {
  const { register } = useCommandRegistry()
  useEffect(() => register(commands), [commands, register])
}

export function CommandProvider({ children }: { children: ReactNode }) {
  const [registry, setRegistry] = useState<Command[][]>([])
  const [open, setOpen] = useState(false)

  const register = useCallback((commands: Command[]) => {
    setRegistry((current) => [...current, commands])
    return () => setRegistry((current) => current.filter((group) => group !== commands))
  }, [])

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault()
        setOpen((current) => !current)
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [])

  const commands = useMemo(() => registry.flat(), [registry])
  const value = useMemo(() => ({ commands, register, open, setOpen }), [commands, register, open])

  return <CommandContext.Provider value={value}>{children}</CommandContext.Provider>
}

export interface CommandPaletteProps {
  /** Results for what has been typed, from outside the shell (features/search). */
  records?: (query: string, close: () => void) => ReactNode
}

export function CommandPalette({ records }: CommandPaletteProps) {
  const { commands, open, setOpen } = useCommandRegistry()
  const [query, setQuery] = useState('')
  // A fresh search each time the palette opens.
  useEffect(() => {
    if (!open) setQuery('')
  }, [open])

  const groups = useMemo(() => {
    const byGroup = new Map<Command['group'], Command[]>()
    for (const command of commands) {
      const list = byGroup.get(command.group) ?? []
      list.push(command)
      byGroup.set(command.group, list)
    }
    return [...byGroup.entries()]
  }, [commands])

  return (
    <CommandDialog open={open} onOpenChange={setOpen} title="Command palette">
      {/* CommandDialog does not supply the cmdk root itself — without this
          wrapper every CommandInput/CommandItem throws on an undefined store. */}
      <CommandRoot>
        <CommandInput
          autoFocus
          placeholder="Search names, numbers and pages…"
          value={query}
          onValueChange={setQuery}
        />
        <CommandList>
          <CommandEmpty>No matches. Try another word.</CommandEmpty>
          {groups.map(([group, list]) => (
            <CommandGroup key={group} heading={group}>
              {list.map((command) => (
                <CommandItem
                  key={command.id}
                  value={`${command.label} ${command.keywords ?? ''}`}
                  onSelect={() => {
                    setOpen(false)
                    command.run()
                  }}
                >
                  {command.icon ? <command.icon className="size-4" /> : null}
                  <span>{command.label}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          ))}
          {records?.(query, () => setOpen(false))}
        </CommandList>
      </CommandRoot>
    </CommandDialog>
  )
}
